import { useEffect, useRef, useState } from 'preact/hooks';
import { api, mediaUrl } from '../api';
import { countryLabel, countryOptions, isKnownCountry } from '../countries';
import type { T } from '../i18n';
import { disablePush, enablePush, pushStatus } from '../push';
import type { PushStatus } from '../push';
import { errorText, getState, refreshAll, set, toast } from '../state';
import type { AppState } from '../state';
import { initialOf, tintFor } from '../util';
import { CameraIcon } from './Icons';

/** Your polaroid and handle on the left, your address book on the right. */
export function Profile({ s, t }: { s: AppState; t: T }) {
  const user = s.user!;
  const avatarInput = useRef<HTMLInputElement>(null);
  const [handle, setHandle] = useState('');

  const avatar = user.avatarMedia ? mediaUrl(user.avatarMedia) : user.avatarUrl || null;
  const canAdd = handle.trim().length > 2;

  // Adding someone puts them straight in the address book: there is nothing
  // for them to accept before you can write.
  const add = async () => {
    const h = handle.trim();
    if (h.length < 3) return;
    try {
      const { friend } = await api.addFriend(h);
      setHandle('');
      toast(t('friendAdded', { who: friend.handle }));
      await refreshAll();
    } catch (err) {
      toast(errorText(err, t('genericError')), 'error');
    }
  };

  return (
    <div class="panel">
      <div class="profile">
        <div class="profile-card">
          <button class="polaroid" aria-label={t('uploadPhoto1')} onClick={() => avatarInput.current?.click()}>
            <div class="polaroid-inner">
              {avatar ? (
                <img src={avatar} alt="" />
              ) : (
                <>
                  <CameraIcon size={30} stroke={2} />
                  <span class="polaroid-cap">
                    <span>{t('uploadPhoto1')}</span>
                    <span>{t('uploadPhoto2')}</span>
                  </span>
                </>
              )}
            </div>
          </button>
          <input
            ref={avatarInput}
            class="visually-hidden"
            type="file"
            accept="image/*"
            tabIndex={-1}
            onChange={(e) => {
              const file = e.currentTarget.files?.[0];
              e.currentTarget.value = '';
              if (file) void uploadAvatar(file, t);
            }}
          />
          <div class="profile-handle">{user.handle}</div>
          <div class="profile-caption">{t('yourHandle')}</div>
          <div class="profile-links">
            <button class="text-link" onClick={() => set({ editOpen: true })}>
              {t('edit')}
            </button>
            <button
              class="text-link"
              onClick={async () => {
                // This device should stop hearing about this person's mail.
                await disablePush().catch(() => undefined);
                await api.logout().catch(() => undefined);
                window.location.href = '/';
              }}
            >
              {t('signOut')}
            </button>
          </div>
          <NotifyToggle t={t} />
        </div>

        <div class="profile-main">
          <div class="kicker">{t('addFriend')}</div>
          <form
            class="friend-add"
            onSubmit={(e) => {
              e.preventDefault();
              void add();
            }}
          >
            <input
              class="field"
              value={handle}
              placeholder="@usuario"
              aria-label={t('addFriend')}
              maxLength={32}
              autocomplete="off"
              autocapitalize="none"
              spellcheck={false}
              onInput={(e) => setHandle(e.currentTarget.value)}
            />
            <button type="submit" class="btn-invite" data-ready={String(canAdd)}>
              {t('invite')}
            </button>
          </form>

          <div class="kicker friends-label">{t('friendsCount', { n: s.friends.length })}</div>
          {s.friends.length === 0 ? (
            <div class="empty">{t('noFriends')}</div>
          ) : (
            <div class="friend-list">
              {s.friends.map((f) => (
                <div key={f.id} class="friend-row">
                  <span class="avatar" style={{ background: tintFor(f.handle) }}>
                    {initialOf(f.handle)}
                  </span>
                  <span class="row-main">
                    <span class="row-handle">{f.handle}</span>
                    <span class="row-sub">{countryLabel(f.country, s.lang)}</span>
                  </span>
                  <span class="friend-status">{t('statusFriends')}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Turns arrival notifications on or off for this browser. */
function NotifyToggle({ t }: { t: T }) {
  const [status, setStatus] = useState<PushStatus | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void pushStatus().then(setStatus, () => setStatus('unsupported'));
  }, []);

  if (!status || status === 'unavailable' || status === 'unsupported') return null;
  if (status === 'install') return <div class="profile-notify-note">{t('notifyInstall')}</div>;
  if (status === 'blocked') return <div class="profile-notify-note">{t('notifyBlocked')}</div>;

  const toggle = async () => {
    setBusy(true);
    try {
      const next = status === 'on' ? await disablePush() : await enablePush();
      setStatus(next);
      if (next === 'on') toast(t('notifyEnabled'));
    } catch (err) {
      toast(errorText(err, t('genericError')), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button class="text-link profile-notify" disabled={busy} aria-pressed={status === 'on'} onClick={toggle}>
      {status === 'on' ? t('notifyTurnOff') : t('notifyTurnOn')}
    </button>
  );
}

async function uploadAvatar(file: File, t: T): Promise<void> {
  try {
    const { id } = await api.upload(file);
    const user = getState().user;
    if (!user) return;
    const { user: updated } = await api.updateMe({
      handle: user.handle,
      country: user.country,
      lang: user.lang,
      avatarMedia: id,
    });
    set({ user: updated });
  } catch (err) {
    toast(errorText(err, t('genericError')), 'error');
  }
}

/**
 * Your handle and country. The design has no place to edit these, so they live
 * behind "editar". Country is picked from a list because delivery times are
 * worked out from it.
 */
export function EditAddress({ s, t }: { s: AppState; t: T }) {
  const user = s.user!;
  const [handle, setHandle] = useState(user.handle);
  const [country, setCountry] = useState(isKnownCountry(user.country) ? user.country : '');
  const [busy, setBusy] = useState(false);
  const close = () => set({ editOpen: false });

  const save = async () => {
    if (!country) return toast(t('chooseCountry'), 'error');
    setBusy(true);
    try {
      const { user: updated } = await api.updateMe({ handle, country, lang: user.lang, countryChosen: true });
      set({ user: updated, editOpen: false });
      toast(t('saved'));
      void refreshAll();
    } catch (err) {
      setBusy(false);
      toast(errorText(err, t('genericError')), 'error');
    }
  };

  return (
    <div
      class="scrim scrim-dialog"
      role="dialog"
      aria-modal="true"
      aria-label={t('editTitle')}
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <form
        class="dialog"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div class="dialog-title">{t('editTitle')}</div>
        <label class="dialog-field" style={{ display: 'block' }}>
          <div class="kicker">{t('handleLabel')}</div>
          <input
            class="field"
            value={handle}
            maxLength={25}
            autocomplete="off"
            autocapitalize="none"
            spellcheck={false}
            onInput={(e) => setHandle(e.currentTarget.value)}
          />
        </label>
        <label class="dialog-field" style={{ display: 'block' }}>
          <div class="kicker">{t('countryLabel')}</div>
          <select class="field" value={country} onChange={(e) => setCountry(e.currentTarget.value)}>
            {!country && (
              <option value="" disabled>
                {t('chooseCountry')}
              </option>
            )}
            {countryOptions(s.lang).map((o) => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <div class="dialog-actions">
          <button type="button" class="btn-line" onClick={close}>
            {t('close')}
          </button>
          <button type="submit" class="btn-ink" disabled={busy}>
            {t('save')}
          </button>
        </div>
      </form>
    </div>
  );
}
